
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { YouTubeChannel } from './types';
import { fetchYouTubeChannels } from '@/services/youtube/data';
import YouTubeChannelSelection from './YouTubeChannelSelection';
import YouTubeAuth from './youtube/YouTubeAuth';
import YouTubeAuthError from './youtube/YouTubeAuthError';
import YouTubeProfileEditor from './youtube/YouTubeProfileEditor';
import YouTubeProfileSelection from './youtube/YouTubeProfileSelection';

interface YouTubeProfileFormProps {
  handlePageChange: (page: string) => void;
}

const YouTubeProfileForm = ({ handlePageChange }: YouTubeProfileFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [channels, setChannels] = useState<YouTubeChannel[]>([]);
  const [accessToken, setAccessToken] = useState<string>("");
  const [refreshToken, setRefreshToken] = useState<string>("");
  const [tokenExpiry, setTokenExpiry] = useState<number>(0);
  const [showChannelSelection, setShowChannelSelection] = useState<boolean>(false);
  
  // Flow state management
  const [currentStep, setCurrentStep] = useState<'auth' | 'profile-selection' | 'channel-selection' | 'profile-editor'>('auth');

  const handleAuthSuccess = async (token: string, refreshToken: string, tokenExpiry: number) => {
    setAccessToken(token);
    setRefreshToken(refreshToken);
    setTokenExpiry(tokenExpiry);
    
    try {
      setLoading(true);
      // Fetch user's YouTube channels
      const userChannels = await fetchYouTubeChannels(token);
      setChannels(userChannels);
      
      setIsAuthenticated(true);
      setLoading(false);
      
      // Move to the appropriate next step based on number of channels
      if (userChannels.length > 1) {
        setCurrentStep('profile-selection');
      } else if (userChannels.length === 1) {
        setCurrentStep('profile-editor');
      } else {
        // If no channels, still go to profile selection to show the error message
        setCurrentStep('profile-selection');
        setErrorMessage("youtubeSignupRequired");
      }
    } catch (error) {
      console.error("Error fetching channels:", error);
      let errorMsg = error instanceof Error ? error.message : "Failed to retrieve YouTube channels. Please try again.";
      
      // Check if it's a YouTube signup required error
      if (typeof error === 'object' && error !== null && 'message' in error) {
        const errorString = String(error.message);
        if (errorString.includes('youtubeSignupRequired')) {
          errorMsg = "youtubeSignupRequired";
        }
      }
      
      setErrorMessage(errorMsg);
      setIsAuthenticated(true); // Still authenticated with Google
      setCurrentStep('profile-selection'); // Go to profile selection to show custom error
      setLoading(false);
    }
  };

  const handleError = (errorMsg: string) => {
    setIsError(true);
    setErrorMessage(errorMsg);
    setLoading(false);
  };

  const handleRetry = () => {
    setIsError(false);
    setErrorMessage("");
    setLoading(true);
    // Small delay before retrying
    setTimeout(() => {
      setCurrentStep('auth');
      setLoading(false);
    }, 500);
  };

  const handleSelectChannel = (channel: YouTubeChannel) => {
    // Move to the profile editor with the selected channel
    setCurrentStep('profile-editor');
  };

  const handleShowAllChannels = () => {
    setCurrentStep('channel-selection');
  };

  // Render appropriate component based on current step and state
  if (isError) {
    return (
      <YouTubeAuthError 
        errorMessage={errorMessage} 
        onRetry={handleRetry} 
        onCancel={() => handlePageChange('add-profile')} 
      />
    );
  }

  switch (currentStep) {
    case 'auth':
      return (
        <YouTubeAuth
          onAuthSuccess={handleAuthSuccess}
          onError={handleError}
          onCancel={() => handlePageChange('add-profile')}
          isLoading={loading}
          setLoading={setLoading}
        />
      );
      
    case 'profile-selection':
      return (
        <YouTubeProfileSelection
          channels={channels}
          email={localStorage.getItem('userEmail') || 'your-email@example.com'}
          onSelectChannel={handleSelectChannel}
          onCancel={() => handlePageChange('add-profile')}
          onShowAllChannels={handleShowAllChannels}
          isLoading={loading}
          error={errorMessage}
        />
      );
      
    case 'channel-selection':
      return (
        <>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center">
              <h2 className="text-lg font-medium">Add a YouTube channel</h2>
            </div>
          </div>
          
          <YouTubeChannelSelection 
            channels={channels}
            email={localStorage.getItem('userEmail') || 'your-email@example.com'}
            onSelectChannel={handleSelectChannel}
            onCancel={() => setCurrentStep('profile-selection')}
            isLoading={loading}
          />
        </>
      );
      
    case 'profile-editor':
      return (
        <YouTubeProfileEditor
          channels={channels}
          accessToken={accessToken}
          refreshToken={refreshToken}
          tokenExpiry={tokenExpiry}
          onProfileSaved={() => handlePageChange('profiles')}
          onCancel={() => handlePageChange('add-profile')}
          onShowChannelSelection={() => setCurrentStep('channel-selection')}
        />
      );
      
    default:
      // Should never reach here, but fallback to auth
      return (
        <YouTubeAuth
          onAuthSuccess={handleAuthSuccess}
          onError={handleError}
          onCancel={() => handlePageChange('add-profile')}
          isLoading={loading}
          setLoading={setLoading}
        />
      );
  }
};

export default YouTubeProfileForm;
